import './formAppearanceStyles.css'

import { ArrowsClockwise, Info } from '@phosphor-icons/react'
import { useIsFetching } from '@tanstack/react-query'
import { Button, ColorPicker, Form, type FormInstance } from 'antd'
import { type Color } from 'antd/es/color-picker'
import { useContext, useMemo } from 'react'
import { useHistory } from 'react-router-dom'

import { QUERIES } from '@/enums/queries'
import { FormBuilderContext } from '@/pages/Contexts/FormBuilderContext'
import { useLoadUserDetails } from '@/queries/users/useLoadUsers'
import FormAntd from '@/ui/form/antd/FormAntd'
import { Section } from '@/ui/organisms/Section'
import { ZentakeContext } from '@/utils/ZentakeContext'

import { FormAppearanceSkeleton } from './FormAppearanceSkeleton'
import { ensureHexString } from './formAppearanceUtils'
import { FormAppearanceModel, useFormAppearance } from './useFormAppearance'

const DEFAULT_COLORS = {
  page: {
    background: '#F3F5F7',
    form: '#FFFFFF',
  },
  font: {
    text: '#374151',
  },
  button: {
    text: '#FFFFFF',
    background: '#1C64F2',
  },
}

export const FormAppearance = () => {
  const history = useHistory()
  const isPdfPage = history.location.pathname.includes('pdf')
  const { handleUpdateAppearanceDetails } = useContext(FormBuilderContext)
  const { teamDetails } = useContext(ZentakeContext)

  const hasAllFormCustomization = teamDetails?.form_customization

  const { data: userDetails, isLoading: isLoadingUserDetails } =
    useLoadUserDetails()
  const { form, rule } = useFormAppearance()
  const isLoadingUpdateTeamConfig = useIsFetching({
    queryKey: [QUERIES.TEAM.UPDATE],
  })

  const updateCssVariables = (values: FormAppearanceModel) => {
    const colors = {
      page: {
        background: hasAllFormCustomization
          ? ensureHexString(values?.page?.background)
          : undefined,
        form: hasAllFormCustomization
          ? ensureHexString(values?.page?.form)
          : undefined,
      },
      font: {
        text: hasAllFormCustomization
          ? ensureHexString(values?.font?.text)
          : undefined,
      },
      button: {
        text: ensureHexString(values?.button?.text),
        background: ensureHexString(values?.button?.background),
      },
    }
    handleUpdateAppearanceDetails(colors)
  }

  const createColorChangeHandler =
    (form: FormInstance<FormAppearanceModel>, path: [string, string]) =>
    (color: Color) => {
      form.setFieldsValue({
        [path[0]]: {
          ...form.getFieldValue(path[0] as keyof FormAppearanceModel),
          [path[1]]: color.toHexString(),
        },
      })
    }

  const initialValues = useMemo(() => {
    const formAppearance = userDetails?.team.config
      ?.form_appearance as FormAppearanceModel

    if (!formAppearance || !Object.keys(formAppearance).length) {
      return DEFAULT_COLORS
    }

    return {
      page: {
        background:
          formAppearance.page?.background || DEFAULT_COLORS.page.background,
        form: formAppearance.page?.form || DEFAULT_COLORS.page.form,
      },
      font: {
        text: formAppearance.font?.text || DEFAULT_COLORS.font.text,
      },
      button: {
        text: formAppearance.button?.text || DEFAULT_COLORS.button.text,
        background:
          formAppearance.button?.background || DEFAULT_COLORS.button.background,
      },
    }
  }, [userDetails?.team.config?.form_appearance])

  let warningMessage =
    'Changes to form colors are applied globally to all forms, not just to one specific form'
  if (isPdfPage) {
    warningMessage =
      'Changes to document colors are applied globally to all documents, not just to one specific document'
  }

  return (
    <div className="relative flex w-full flex-col items-center gap-6 px-10 pt-6">
      {isLoadingUserDetails ? (
        <>
          <FormAppearanceSkeleton />
          <FormAppearanceSkeleton />
          <FormAppearanceSkeleton />
        </>
      ) : (
        <>
          <div className="flex w-full max-w-[1100px] items-center justify-between">
            <Section.Title className="text-2xl font-bold">
              Design Settings
            </Section.Title>
            <Button
              className="h-9 border-gray-200 bg-white/80 px-3 text-sm text-primary shadow-none !outline-none"
              disabled={Boolean(isLoadingUpdateTeamConfig)}
              onClick={() => {
                form.setFieldsValue(DEFAULT_COLORS)
                updateCssVariables(DEFAULT_COLORS)
              }}
            >
              <ArrowsClockwise className="text-base" weight="bold" /> Return to
              default colors
            </Button>
          </div>
          <div className="flex w-full max-w-[1100px] items-center justify-start gap-2 rounded border border-solid !border-purple-200 bg-purple-100 px-4 py-2.5 text-center font-medium text-purple-600">
            <Info className="text-lg" weight="bold" /> {warningMessage}
          </div>
          <FormAntd.Root<FormAppearanceModel>
            className="relative flex h-full w-full max-w-[1100px] flex-col gap-6 pb-6"
            form={form}
            initialValues={initialValues}
            layout="vertical"
            onValuesChange={(_, allValues) => updateCssVariables(allValues)}
            validateTrigger={['onBlur']}
          >
            {hasAllFormCustomization ? (
              <>
                <Section.Root>
                  <Section.Header>
                    <Section.Title className="header-1">Page</Section.Title>
                    <Section.Description>
                      Customize your own brand color into your form page.
                    </Section.Description>
                  </Section.Header>
                  <Section.Divider />
                  <Section.Content>
                    <div className="flex items-end">
                      <Form.Item<FormAppearanceModel>
                        className="[&_label]:whitespace-nowrap"
                        label="Background color"
                        name={['page', 'background']}
                      >
                        <ColorPicker
                          onChange={createColorChangeHandler(form, [
                            'page',
                            'background',
                          ])}
                          size="large"
                        />
                      </Form.Item>
                      <Form.Item<FormAppearanceModel>
                        className="-ml-24 mb-2.5 w-full"
                        name={['page', 'background']}
                        rules={[rule]}
                      >
                        <FormAntd.Input />
                      </Form.Item>
                    </div>
                    <div className="flex items-end">
                      <Form.Item<FormAppearanceModel>
                        className="[&_label]:whitespace-nowrap"
                        label="Form color"
                        name={['page', 'form']}
                      >
                        <ColorPicker
                          onChange={createColorChangeHandler(form, [
                            'page',
                            'form',
                          ])}
                          size="large"
                        />
                      </Form.Item>
                      <Form.Item<FormAppearanceModel>
                        className="-ml-11 mb-2.5 w-full"
                        name={['page', 'form']}
                        rules={[rule]}
                      >
                        <FormAntd.Input />
                      </Form.Item>
                    </div>
                  </Section.Content>
                </Section.Root>

                <Section.Root>
                  <Section.Header>
                    <Section.Title className="header-2">Font</Section.Title>
                    <Section.Description>
                      Customize the font style with your own brand color.
                    </Section.Description>
                  </Section.Header>
                  <Section.Divider />
                  <Section.Content>
                    <div className="flex items-end">
                      <Form.Item<FormAppearanceModel>
                        className="[&_label]:whitespace-nowrap"
                        label="Question Text color"
                        name={['font', 'text']}
                      >
                        <ColorPicker
                          onChange={createColorChangeHandler(form, [
                            'font',
                            'text',
                          ])}
                          size="large"
                        />
                      </Form.Item>
                      <Form.Item<FormAppearanceModel>
                        className="-ml-[109px] mb-2.5 w-full"
                        name={['font', 'text']}
                        rules={[rule]}
                      >
                        <FormAntd.Input />
                      </Form.Item>
                    </div>
                  </Section.Content>
                </Section.Root>
              </>
            ) : null}

            <Section.Root className="mb-6">
              <Section.Header>
                <Section.Title className="header-3">Button</Section.Title>
                <Section.Description>
                  Customize the button style with your own brand color.
                </Section.Description>
              </Section.Header>
              <Section.Divider />
              <Section.Content>
                <div className="flex items-end">
                  <Form.Item<FormAppearanceModel>
                    className="[&_label]:whitespace-nowrap"
                    label="Button Text color"
                    name={['button', 'text']}
                  >
                    <ColorPicker
                      onChange={createColorChangeHandler(form, [
                        'button',
                        'text',
                      ])}
                      size="large"
                    />
                  </Form.Item>
                  <Form.Item<FormAppearanceModel>
                    className="-ml-[92px] mb-2.5 w-full"
                    name={['button', 'text']}
                    rules={[rule]}
                  >
                    <FormAntd.Input />
                  </Form.Item>
                </div>
                <div className="flex items-end">
                  <Form.Item<FormAppearanceModel>
                    className="[&_label]:whitespace-nowrap"
                    label="Button color"
                    name={['button', 'background']}
                  >
                    <ColorPicker
                      onChange={createColorChangeHandler(form, [
                        'button',
                        'background',
                      ])}
                      size="large"
                    />
                  </Form.Item>
                  <Form.Item<FormAppearanceModel>
                    className="-ml-[56px] mb-2.5 w-full"
                    name={['button', 'background']}
                    rules={[rule]}
                  >
                    <FormAntd.Input />
                  </Form.Item>
                </div>
              </Section.Content>
            </Section.Root>
          </FormAntd.Root>
        </>
      )}
    </div>
  )
}
